/**
 * Implementation based on DndKit Sortable Multiple Containers
 * https://master--5fc05e08a4a65d0021ae0bf2.chromatic.com/?path=/story/presets-sortable-multiple-containers--basic-setup
 */
import { useCallback, useEffect, useRef, useState, ReactNode, FC } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  defaultDropAnimationSideEffects,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { multipleContainersCoordinateGetter } from './multipleContainersKeyboardCoordinates';

import { DraggableItem } from './DraggableItem';
import { TaskStatus } from '@nexelem/graphne-api';
import { parseColumnId } from '../utils';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

export type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

export type KanbanDndPropsChilrenProps = {
  items: Items;
  isSortingContainer: boolean;
};

type MoveType =
  | {
      type: 'status';
      id: number;
      from: TaskStatus;
      to: TaskStatus;
      afterId?: number;
      queueId: number;
    }
  | { type: 'sort'; id: number; afterId?: number; queueId?: number };

export type OnDragEnd = (move: MoveType) => void;

type KanbanDndProps = {
  items: Items;
  OverlayComponent: FC<{ id: number }>;
  children?: (props: KanbanDndPropsChilrenProps) => ReactNode;
  onDragEnd: OnDragEnd;
};

export const KanbanDnd = ({
  items: initialItems,
  OverlayComponent,
  children,
  onDragEnd: externalDragEnd,
}: KanbanDndProps) => {
  const [items, setItems] = useState<Items>(initialItems);
  const [containers] = useState(Object.keys(items) as UniqueIdentifier[]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId != null ? containers.includes(activeId) : false;

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (overId in items) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems?.length && containerItems?.length > 0) {
            // Return the closest droppable within that container
            overId =
              closestCenter({
                ...args,
                droppableContainers: args.droppableContainers.filter(
                  (container) => container.id !== overId && containerItems.includes(container.id),
                ),
              })[0]?.id || null;
          }
        }

        lastOverId.current = overId;

        return lastOverId.current ? [{ id: lastOverId.current }] : [];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );
  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: multipleContainersCoordinateGetter,
    }),
  );
  const findContainer = (id: UniqueIdentifier): UniqueIdentifier | undefined => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key]?.includes(id));
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const activeContainer = findContainer(active.id);
    const overId = over?.id;
    const { queueId } = parseColumnId(over?.data.current?.columnId);

    /**
     * Handle visual sorting
     */
    if (!activeContainer || !overId) {
      setActiveId(null);
      return;
    }

    const overContainer = findContainer(overId);

    if (overContainer) {
      const activeItems = items[activeContainer] ?? [];
      const overItems = items[overContainer] ?? [];
      const activeIndex = activeItems.indexOf(active.id);
      const overIndex = overItems.indexOf(overId);

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        setItems((items) => {
          return {
            ...items,
            [overContainer]: arrayMove(items[overContainer] ?? [], activeIndex, overIndex),
          };
        });
      }
    }

    setActiveId(null);

    /**
     * Handle endpoint change
     */
    const fromStatus = active?.data.current?.taskStatus as TaskStatus;
    const { status: toStatus } = parseColumnId(active?.data.current?.sortable?.containerId);
    const isFirst = over?.data?.current?.sortable?.items?.[0] === overId;

    // Change task status
    if (fromStatus && toStatus && fromStatus !== toStatus) {
      externalDragEnd({
        type: 'status',
        id: active.id as number,
        from: fromStatus,
        to: toStatus,
        afterId: isFirst ? undefined : (overId as number),
        queueId: Number(queueId),
      });

      onDragCancel();
    } else if (overContainer) {
      externalDragEnd({
        type: 'sort',
        id: active.id as number,
        afterId: isFirst ? undefined : (overId as number),
        queueId: Number(queueId),
      });
    }
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    const overId = over?.id;

    if (overId == null || active.id in items) {
      return;
    }

    const overContainer = findContainer(overId);
    const activeContainer = findContainer(active.id);

    if (!overContainer || !activeContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      setItems((items) => {
        const activeItems = items[activeContainer] ?? [];
        const overItems = items[overContainer] ?? [];
        const overIndex = overItems.indexOf(overId);
        const activeIndex = activeItems.indexOf(active.id);

        if (activeIndex === -1) {
          return items;
        }

        let newIndex: number;

        if (overId in items) {
          newIndex = overItems.length + 1;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top > over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;
          newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        recentlyMovedToNewContainer.current = true;

        return {
          ...items,
          [activeContainer]: activeItems.filter((item) => item !== active.id),
          [overContainer]: [
            ...overItems.slice(0, newIndex),
            activeItems[activeIndex],
            ...overItems.slice(newIndex),
          ] as UniqueIdentifier[],
        };
      });
    }
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setClonedItems(items);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  return (
    <DndContext
      collisionDetection={collisionDetectionStrategy}
      sensors={sensors}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
    >
      {children?.({ isSortingContainer, items })}
      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeId && (
            <DraggableItem dragOverlay>
              <OverlayComponent id={activeId as number} />
            </DraggableItem>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
